import { possessive } from '@shared';
import { Badge, cn } from '@design-system';
import { LtcAtAgeSlideOutContent, LikelihoodSlideOutContent } from '@/components';
import { MethodologyCallout } from '@/shared/components/methodology-callout';
import { QuickEstimateBadge } from '@/shared/components/quick-estimate-badge';
import {
  Citations,
  AnimationToggle,
  OnboardingLayout,
  FunctionalStateAnimation,
  useSlideSetup,
  useAnimationPreferences,
  citations,
} from '@/features/client-onboarding';

import { useSelector } from 'react-redux';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

import {
  selectClient,
  selectInferenceSet,
  selectIntakeSurvey,
  selectAppliedInferenceSet,
} from '@/model';
import { startCase } from 'lodash';
import { isInferenceEdited, possessivePronoun } from '@/util';

import moduleStyles from './OnboardingOurPredictions.module.css';
import mainStyles from '@/styles/main.module.css';

const styles = {
  ...mainStyles,
  ...moduleStyles,
};

export function OnboardingOurPredictions(_props: ClientIdProps) {
  const { flags } = useFeatureFlags();
  const client = useSelector(selectClient);

  const {
    clientFirstName,
    appliedInferenceSet: { ltcAtAgeShowRange, ltcAtAge },
  } = client;

  const intakeSurvey = useSelector(selectIntakeSurvey);
  const { ltcLikelihoodEver } = useSelector(selectInferenceSet);
  const { animationsEnabled } = useAnimationPreferences();

  const showAnimations = flags.animatedSlides && animationsEnabled;

  useSlideSetup({
    slidePage: OnboardingOurPredictions,
    nextButtonLabel: `${possessive(clientFirstName)} Care Journey`,
  });

  return (
    <OnboardingLayout>
      <div className="flex h-max flex-col items-center justify-center pb-4">
        <div className="w-full py-2 md:py-8">
          <div className="mb-6 flex w-full grow items-center justify-between gap-6">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-balance text-2xl font-bold text-gray-950 md:text-4xl">
                {possessive(clientFirstName)} Care Journey
              </h1>
              <QuickEstimateBadge />
            </div>
            {flags.animatedSlides && (
              <div className="flex items-center gap-2">
                <AnimationToggle />
              </div>
            )}
          </div>
          {showAnimations ? (
            <FunctionalStateAnimation
              client={client}
              ltcLikelihood={ltcLikelihoodEver}
              ltcAtAge={ltcAtAge}
              intakeSurvey={intakeSurvey}
              ltcAtAgeIsEdited={isInferenceEdited(client, 'ltcAtAge')}
            />
          ) : (
            <div className="flex w-full grow flex-col gap-4 sm:flex-row">
              <ColumnLtcLikelihoodEver />
              <ColumnLtcAtAge />
              <ColumnLtcAgeFallBack />
            </div>
          )}
        </div>
        <div className="mt-auto w-full pt-4">
          <Citations
            citations={
              ltcAtAgeShowRange
                ? [
                    citations.onboardingOurPredictionsLikelihoodNationalAverage,
                    citations.onboardingOurPredictionsLtcAtAgeRange,
                  ]
                : [citations.onboardingOurPredictionsLikelihoodNationalAverage]
            }
            className="w-full rounded-lg"
          />
        </div>
      </div>
    </OnboardingLayout>
  );
}

export function ColumnLtcLikelihoodEver() {
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const { ltcLikelihoodEver } = useSelector(selectInferenceSet);
  return (
    <div
      className="border-primary-200 flex grow basis-1/2 flex-col justify-between rounded-2xl border p-4 text-center md:p-6"
      data-testid="predictions-likelihood-column"
    >
      <p
        className="text-foreground text-base font-medium md:text-xl"
        data-testid="predictions-likelihood-label"
      >
        {startCase(possessivePronoun(intakeSurvey))} likelihood of needing LTC{' '}
        <MethodologyCallout
          content={<LikelihoodSlideOutContent />}
          title="AI"
          variant="icon"
          iconClassName="inline md:size-5"
        />
      </p>
      <p
        className="text-primary-dark py-4 text-5xl font-semibold leading-normal md:text-9xl"
        data-testid="predictions-likelihood-value"
      >
        {Math.round(ltcLikelihoodEver * 100)}
        <span className={cn(styles.percentSymbol, 'leading-none')}>%</span>
      </p>
      <p
        className="text-muted-foreground text-base font-normal md:text-xl"
        data-testid="predictions-likelihood-benchmark"
      >
        U.S. Average at age 65: 56%<sup>1</sup>
      </p>
    </div>
  );
}

export function ColumnLtcAtAge() {
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const { ltcAtAge, ltcAtAgeShowRange } = useSelector(selectAppliedInferenceSet);
  const client = useSelector(selectClient);

  if (ltcAtAgeShowRange) {
    return null;
  }

  return (
    <div
      className="border-primary-200 flex grow basis-1/2 flex-col justify-between rounded-2xl border p-6 text-center"
      data-testid="predictions-age-column"
    >
      <p
        className="text-foreground text-base font-medium md:text-xl"
        data-testid="predictions-age-label"
      >
        {startCase(possessivePronoun(intakeSurvey))} care is expected to begin at{' '}
        <MethodologyCallout
          content={<LtcAtAgeSlideOutContent />}
          title="AI"
          variant="icon"
          iconClassName="inline md:size-5"
        />
      </p>
      <p
        className="text-primary-dark py-4 text-5xl font-semibold leading-normal md:text-9xl"
        data-testid="predictions-age-value"
      >
        {ltcAtAge}
      </p>
      <p className="text-muted-foreground flex justify-center gap-2 text-base font-normal md:text-xl">
        <span data-testid="predictions-age-unit">years old</span>
        {isInferenceEdited(client, 'ltcAtAge') && <Badge variant="secondary">edited</Badge>}
      </p>
    </div>
  );
}

export function ColumnLtcAgeFallBack() {
  const intakeSurvey = useSelector(selectIntakeSurvey);
  const { ltcAtAge, ltcAtAgeShowRange } = useSelector(selectAppliedInferenceSet);
  const client = useSelector(selectClient);

  if (!ltcAtAgeShowRange) {
    return null;
  }

  return (
    <div className="border-primary-200 flex grow basis-1/2 flex-col justify-between rounded-2xl border p-4 text-center md:p-6">
      <p className="text-foreground text-base font-medium md:text-xl">
        {startCase(possessivePronoun(intakeSurvey))} care is expected to begin at{' '}
        <sup className="text-muted-foreground font-normal">2</sup>
      </p>
      <p className="text-primary-dark py-4 text-5xl font-semibold leading-normal md:text-9xl">
        {ltcAtAge}
      </p>
      <p className="text-muted-foreground flex justify-center gap-2 text-base font-normal md:text-xl">
        <span>years old</span>
        {isInferenceEdited(client, 'ltcAtAge') && <Badge variant="secondary">edited</Badge>}
      </p>
    </div>
  );
}
